import * as React from 'react'
import { AutoComplete } from 'antd'
import { AutoCompleteProps } from 'antd/lib/auto-complete'
import { getProfiles, Profile } from '../DIS/api'
import { taskEither, task } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>

const getName = (p: Profile) => {
  if (!p.firstName && !p.lastName) {
    return p.nym
  }
  return `${p.firstName ?? ''} ${p.lastName ?? ''}`
}

const defaultTransform = (p: Profile) => ({ label: getName(p), value: p.nym })

const defaultSearch = (t: string) =>
  pipe(
    getProfiles(0, 5, t),
    taskEither.getOrElse(() => task.of({ count: 0, profiles: [] as Profile[] }))
  )

type OptionsType = {
  value: string
  label: string
}

type ACProps<T> =
  | {
      type?: 'default'
      transform?: (_: Profile) => OptionsType
    }
  | {
      type: 'custom'
      onSearch: (_: string) => Promise<T[]>
      transform: (_: T) => OptionsType
    }

export type Props<T = string> = ACProps<T> &
  Omit<AutoCompleteProps, 'onSearch' | 'dataSource'>

function searchFunc<T = string>(
  p: ACProps<T>
): (_: string) => Promise<OptionsType[]> {
  if (p.type !== 'custom') {
    const { transform = defaultTransform } = p

    return (t: string) =>
      defaultSearch(t)().then(d => d.profiles.map(transform))
  }

  return (t: string) => p.onSearch(t).then(vs => vs.map(p.transform))
}

function NymAutocomplete<T>(p: Props<T>) {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [searchResults, setSearchResults] = React.useState<OptionsType[]>([])
  const [selected, setSelected] = React.useState<OptionsType | undefined>(
    undefined
  )

  const { onSelect, ...rest } = p

  React.useEffect(() => {
    !!searchTerm
      ? searchFunc<T>(p)(searchTerm).then(setSearchResults)
      : setSearchResults([])
  }, [searchTerm, p])

  return (
    <AutoComplete
      onSearch={s => {
        setSelected(undefined)
        setSearchTerm(s)
      }}
      options={searchResults}
      onSelect={(value: any, o: any) => {
        onSelect?.(value, o)
        // @ts-ignore
        setSelected(o)
      }}
      {...rest}
      value={selected?.label}
    />
  )
}

export default NymAutocomplete
